import { z } from "zod";

export const SPANISH_JOURNEY_PLAN_VERSION = 1;

export const SPANISH_JOURNEY_LANGUAGE_CODE = "es-ES";

export const SPANISH_JOURNEY_PROGRESS_NAMESPACE = "spanish-v3";

export const SpanishJourneyGoalSchema = z.enum([
  "everyday",
  "travel",
  "work",
  "relationships",
  "media",
  "study",
]);

export const SpanishJourneyAbilitySchema = z.enum([
  "starting_out",
  "basic_building_blocks",
  "everyday_conversations",
  "confident_conversations",
  "advanced_expression",
]);

export const SpanishJourneyTeachingApproachSchema = z.enum([
  "guided_discovery",
  "explain_then_practice",
  "examples_first",
  "challenge_me",
]);

export const SpanishJourneyStuckStrategySchema = z.enum([
  "hint",
  "smaller_steps",
  "another_example",
  "realistic_situation",
  "retry_before_help",
]);

export const SpanishJourneyCorrectionTimingSchema = z.enum([
  "immediate",
  "after_attempt",
  "only_blocking",
]);

export const SpanishJourneyLanguageSupportSchema = z.enum([
  "mostly_spanish",
  "balanced",
  "more_english",
]);

export const SpanishJourneyChallengeLevelSchema = z.enum(["comfortable", "focused", "intensive"]);

export const SpanishJourneyTimeSchema = z.enum(["two_weeks", "one_month", "six_weeks", "flexible"]);

export const SpanishJourneyGoalProfileBaseSchema = z.object({
  goal: SpanishJourneyGoalSchema,
  ability: SpanishJourneyAbilitySchema,
  startingChapterId: z.string().trim().min(1).optional(),
  teachingApproach: SpanishJourneyTeachingApproachSchema.default("guided_discovery"),
  stuckStrategy: SpanishJourneyStuckStrategySchema.default("hint"),
  correctionTiming: SpanishJourneyCorrectionTimingSchema.default("after_attempt"),
  languageSupport: SpanishJourneyLanguageSupportSchema.default("balanced"),
  challengeLevel: SpanishJourneyChallengeLevelSchema.default("focused"),
  timeAvailable: SpanishJourneyTimeSchema,
  goalDetail: z.string().trim().max(160).optional().default(""),
});

export type SpanishJourneyGoalProfile = z.infer<typeof SpanishJourneyGoalProfileBaseSchema>;

export const SpanishJourneyPlanItemSchema = z.object({
  chapterId: z.string().min(1),
  milestone: z.string().trim().min(1).max(80),
  reason: z.string().trim().min(1).max(240),
  applicationContext: z.string().trim().min(1).max(180),
});

export const SpanishJourneyPlanBaseSchema = z.object({
  version: z.literal(SPANISH_JOURNEY_PLAN_VERSION),
  languageCode: z.literal(SPANISH_JOURNEY_LANGUAGE_CODE),
  progressNamespace: z.literal(SPANISH_JOURNEY_PROGRESS_NAMESPACE),
  catalogVersion: z.number().int().positive(),
  courseVersion: z.number().int().positive(),
  title: z.string().trim().min(1).max(80),
  estimatedSessions: z.number().int().min(6).max(10),
  estimatedWeeks: z.number().int().min(1).max(12),
  status: z.enum(["draft", "active"]),
  profile: SpanishJourneyGoalProfileBaseSchema,
  items: z.array(SpanishJourneyPlanItemSchema).min(6).max(10),
  createdAt: z.string().datetime({ offset: true }),
  updatedAt: z.string().datetime({ offset: true }),
});

export type SpanishJourneyPlan = z.infer<typeof SpanishJourneyPlanBaseSchema>;

export type SpanishJourneyPlanItem = z.infer<typeof SpanishJourneyPlanItemSchema>;

export const START_CHAPTER_BY_ABILITY: Record<SpanishJourneyGoalProfile["ability"], number> = {
  starting_out: 1,
  basic_building_blocks: 18,
  everyday_conversations: 36,
  confident_conversations: 58,
  advanced_expression: 78,
};

export const PLAN_SHAPE_BY_TIME: Record<
  SpanishJourneyGoalProfile["timeAvailable"],
  { sessions: number; weeks: number }
> = {
  two_weeks: { sessions: 8, weeks: 4 },
  one_month: { sessions: 8, weeks: 4 },
  six_weeks: { sessions: 8, weeks: 4 },
  flexible: { sessions: 8, weeks: 4 },
};
